import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  ButtonGroup,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as XLSX from "xlsx";
import api from "../api"; // your axios instance

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch all orders once
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get("/orders");
        const dataWithId = response.data.map((order, idx) => ({
          id: order.billNumber ?? `${idx}-${order.prodName}-${order.aTime}`,
          ...order,
        }));
        setOrders(dataWithId);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  // Normalize date string (YYYY-MM-DD)
  const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  // Format as YYYY-MM-DD in local time
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayStr = formatDate(new Date());


  // Apply filter
  const filteredOrders = orders.filter((order) => {
    const orderDateStr = formatDate(order.aDate);
    const today = new Date();

    if (filter === "Today") {
      return orderDateStr === todayStr;
    }
    if (filter === "Weekly") {
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      return new Date(order.aDate) >= weekAgo && new Date(order.aDate) <= today;
    }
    if (filter === "Monthly") {
      const d = new Date(order.aDate);
      return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
    }
    return true; // All
  });

  // Export filtered data
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredOrders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${filter} Orders`);
    XLSX.writeFile(workbook, `Orders_${filter}.xlsx`);
  };

  // Handler for View button → fetch full details by mainID
  const handleView = async (mainID) => {
    setDetailsLoading(true);
    setOpenDialog(true);
    try {
      const response = await api.get(`/orders/GetOrderFullDetails/${mainID}`, {
        headers: {
          Authorization: `Bearer YOUR_JWT_TOKEN_HERE`, // replace with your token
          Accept: "text/plain"
        }
      });
      setOrderDetails(response.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
      setOrderDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Columns (use flex for responsiveness)
  const columns = [
    { field: "mainID", headerName: "MainID", flex: 1, minWidth: 100 },
    { field: "custName", headerName: "Customer Name", flex: 1, minWidth: 150 },
    { field: "custPhone", headerName: "Phone", flex: 1, minWidth: 120 },
    {
      field: "aDate",
      headerName: "Date",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    { field: "aTime", headerName: "Time", flex: 1, minWidth: 100 },
    { field: "orderType", headerName: "Order Type", flex: 1, minWidth: 120 },
    { field: "paymentMode", headerName: "Payment Mode", flex: 1, minWidth: 120 },
    { field: "total", headerName: "Amount(₹)", flex: 1, minWidth: 100 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 100 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleView(params.row.mainID)}
            sx={{ mr: 1, mt: 0.5 }}
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" mb={2} textAlign="center">
        Orders Management
      </Typography>

      {/* Tabs + Export aligned responsively */}
      <Box
        display="flex"
        flexDirection={isSmallScreen ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isSmallScreen ? "stretch" : "center"}
        mb={2}
        gap={2}
      >
        <ButtonGroup
          variant="contained"
          orientation={isSmallScreen ? "vertical" : "horizontal"}
          fullWidth={isSmallScreen}
        >
          {["Today", "Weekly", "Monthly", "All"].map((tab) => (
            <Button
              key={tab}
              color={filter === tab ? "error" : "primary"}
              onClick={() => setFilter(tab)}
            >
              {tab}
            </Button>
          ))}
        </ButtonGroup>

        <Button
          variant="contained"
          color="success"
          onClick={exportToExcel}
          fullWidth={isSmallScreen}
        >
          Export {filter} Orders to Excel
        </Button>
      </Box>

      {/* Loading spinner */}
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
          <CircularProgress size={60} />
          <Typography variant="h6" mt={2}>
            Please wait, data is loading...
          </Typography>
        </Box>
      ) : (
        <DataGrid
          rows={[...filteredOrders]}
          columns={columns}
          autoHeight
          pageSize={5}
          rowsPerPageOptions={[5, 10]}
          disableSelectionOnClick
        />
      )}

      {/* Responsive Dialog */}
     <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
  <DialogTitle sx={{ backgroundColor: "#cf2e2e", color: "white", textAlign: "center" }}>
    Order Receipt
  </DialogTitle>

  <DialogContent dividers>
    {detailsLoading ? (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress />
      </Box>
    ) : orderDetails && orderDetails.length > 0 ? (
      <Box sx={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr style={{ backgroundColor: "#f5f5f5" }}>
              <th style={{ border: "1px solid #ddd", padding: "6px" }}>Product</th>
              <th style={{ border: "1px solid #ddd", padding: "6px" }}>Qty</th>
              <th style={{ border: "1px solid #ddd", padding: "6px" }}>Price</th>
              <th style={{ border: "1px solid #ddd", padding: "6px" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {orderDetails.map((detail, idx) => (
              <tr key={idx} style={{ textAlign: "center" }}>
                <td style={{ border: "1px solid #ddd", padding: "6px" }}>{detail.pName}</td>
                <td style={{ border: "1px solid #ddd", padding: "6px" }}>{detail.pQty}</td>
                <td style={{ border: "1px solid #ddd", padding: "6px" }}>₹{detail.pPrice}</td>
                <td style={{ border: "1px solid #ddd", padding: "6px" }}>₹{detail.pAmount}</td>
              </tr>
            ))}
          </tbody>
          {/* ✅ Single total row at the bottom */}
          <tfoot>
  <tr style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}>
    {/* Instead of colSpan=3, use colSpan=2 and add some padding/margin */}
    <td colSpan={2} style={{ border: "1px solid #ddd", padding: "6px", textAlign: "left" }}>
      Total
    </td>
    {/* Empty cell for spacing */}
    <td style={{ border: "1px solid #ddd", padding: "4px" }}></td>
    {/* Amount cell */}
    <td style={{ border: "1px solid #ddd", padding: "10px",paddingRight: "40px", textAlign: "right" }}>
      ₹{orderDetails[0].total}
    </td>
  </tr>
</tfoot>

        </table>
      </Box>
    ) : (
      <Typography>No details found</Typography>
    )}
  </DialogContent>

  <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
  <Button
    onClick={() => setOpenDialog(false)}
    variant="contained"
    sx={{
      backgroundColor: "#cf2e2e",
      color: "white",
      "&:hover": {
        backgroundColor: "#b22222", // darker shade on hover
      },
    }}
  >
    Close
  </Button>
</DialogActions>

</Dialog>
    </Box>
  );
}

export default Orders;